import React from "react";
import { Typography } from "@material-ui/core";
import dayjs from "dayjs";
import {
  ButtonWrap,
  formatTripDates,
  Icon,
  IconName,
  RecentlySearchedList,
} from "halifax";
import { RecentExperienceSearch } from "redmond";

import { TripDestination } from "../../types";

import "./styles.scss";

export interface TripDestinationsSectionProps {
  tripDestinations: TripDestination[];
  loading: boolean;
  onTripClick: (trip: TripDestination, index: number) => void;
  recentSearches?: RecentExperienceSearch[];
  onRecentSearchClick?: (search: RecentExperienceSearch) => void;
  searchNearby?: (latitude: number, longitude: number) => void;
  searchNearbyLocation?: string;
  handleSearchNearbyClick?: () => void;
}

const getTripStatusLabel = (
  tripDate: string,
  tripEndDate: string
): string | null => {
  const today = dayjs().startOf("day");
  const start = dayjs(tripDate).startOf("day");
  const end = dayjs(tripEndDate).startOf("day");

  if ((today.isSame(start) || today.isAfter(start)) && today.isBefore(end.add(1, "day"))) {
    return "Ongoing";
  }

  const daysAway = start.diff(today, "day");
  if (daysAway > 0) {
    return `${daysAway} day${daysAway === 1 ? "" : "s"} away`;
  }

  return null;
};

const TripDestinationItem = ({
  trip,
  onClick,
  showStatusLabel,
}: {
  trip: TripDestination;
  onClick: () => void;
  showStatusLabel?: boolean;
}) => {
  const iconName =
    trip.source === "Flight"
      ? IconName.TripFlightIcon
      : IconName.TripHotelIcon;
  const statusLabel = showStatusLabel
    ? getTripStatusLabel(trip.tripDate, trip.tripEndDate)
    : null;

  return (
    <ButtonWrap
      className="trip-destination-item"
      onClick={onClick}
      tabIndex={-1}
    >
      <div className="trip-destination-icon">
        <div className="icon-container">
          <Icon name={iconName} />
        </div>
      </div>
      <div className="trip-destination-details">
        <Typography className="trip-destination-name">
          {trip.displayName}
        </Typography>
        <Typography className="trip-destination-dates">
          {formatTripDates(trip.tripDate, trip.tripEndDate)}
        </Typography>
        {statusLabel && (
          <Typography className="trip-destination-status">
            {statusLabel}
          </Typography>
        )}
      </div>
    </ButtonWrap>
  );
};

const LoadingText = () => {
  return (
    <Typography className="trip-destinations-loading-text">
      Loading...
    </Typography>
  );
};

export const TripDestinationsSection = ({
  tripDestinations,
  loading,
  onTripClick,
  recentSearches,
  onRecentSearchClick,
  searchNearby,
  searchNearbyLocation,
  handleSearchNearbyClick,
}: TripDestinationsSectionProps) => {
  const hasTrips = loading || tripDestinations.length > 0;
  const hasRecentSearches =
    recentSearches && recentSearches.length > 0 && onRecentSearchClick;

  if (!hasTrips && !hasRecentSearches) {
    return null;
  }

  return (
    <div className="trip-destinations-section">
      <div className="trip-destinations-scroll-container">
        {hasTrips && (
          <>
            <Typography className="trip-destinations-heading">
              Your upcoming trips
            </Typography>
            {loading ? (
              <LoadingText />
            ) : (
              <div className="trip-destinations-list">
                {tripDestinations.map((trip, index) => (
                  <TripDestinationItem
                    key={`${trip.location.id}-${index}`}
                    trip={trip}
                    onClick={() => onTripClick(trip, index)}
                    showStatusLabel={index === 0}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {hasRecentSearches && (
          <RecentlySearchedList
            recentSearches={recentSearches}
            onRecentSearchClick={(search) =>
              onRecentSearchClick(search as RecentExperienceSearch)
            }
            searchNearby={searchNearby}
            searchNearbyLocation={searchNearbyLocation}
            handleSearchNearbyClick={handleSearchNearbyClick}
          />
        )}
      </div>
    </div>
  );
};
